import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import type { DevicePlacement } from './devicePlacement'
import { ReactComponent as CheckIcon } from 'assets/icons/Check.svg'
import { ReactComponent as ChevronDownIcon } from 'assets/icons/ChevronDown.svg'

import { getDevicePlacement } from './devicePlacement'
import styles from './deviceMenu.module.scss'

export type DeviceKind = 'audioinput' | 'videoinput'

const TITLE_KEY = {
  audioinput: 'chat.call.deviceMenu.microphone',
  videoinput: 'chat.call.deviceMenu.camera'
} as const

const UNNAMED_KEY = {
  audioinput: 'chat.call.deviceMenu.unnamedMicrophone',
  videoinput: 'chat.call.deviceMenu.unnamedCamera'
} as const

export type DeviceMenuProps = {
  kind: DeviceKind
  devices: MediaDeviceInfo[]
  activeDeviceId?: string
  disabled?: boolean
  iconSize?: number
  triggerClassName?: string
  onSelect: (deviceId: string) => void
}

export function DeviceMenu({
  kind,
  devices,
  activeDeviceId,
  disabled,
  iconSize = 14,
  triggerClassName,
  onSelect
}: DeviceMenuProps) {
  const { t } = useTranslation()

  const [isOpen, setIsOpen] = useState(false)
  const [placement, setPlacement] = useState<DevicePlacement>()

  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const title = t(TITLE_KEY[kind])

  const selectedDeviceId = devices.some((device) => device.deviceId === activeDeviceId)
    ? activeDeviceId
    : devices[0]?.deviceId

  useEffect(() => {
    if (disabled) {
      setIsOpen(false)
    }
  }, [disabled])

  useLayoutEffect(() => {
    if (!isOpen) {
      return
    }

    const update = () => {
      const anchor = triggerRef.current?.getBoundingClientRect()

      if (anchor) {
        setPlacement(
          getDevicePlacement(anchor, { width: window.innerWidth, height: window.innerHeight })
        )
      }
    }

    update()

    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)

    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const handlePointerDown = ({ target }: MouseEvent) => {
      const node = target as Node

      if (!menuRef.current?.contains(node) && !triggerRef.current?.contains(node)) {
        setIsOpen(false)
      }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        setIsOpen(false)
        triggerRef.current?.focus()
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  const handleSelect = useCallback(
    (deviceId: string) => {
      setIsOpen(false)
      onSelect(deviceId)
    },
    [onSelect]
  )

  const deviceLabel = (device: MediaDeviceInfo, index: number) =>
    device.label || t(UNNAMED_KEY[kind], { number: index + 1 })

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={triggerClassName ? `${styles.trigger} ${triggerClassName}` : styles.trigger}
        onClick={() => setIsOpen((open) => !open)}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={title}
      >
        <ChevronDownIcon width={iconSize} height={iconSize} aria-hidden />
      </button>

      {isOpen &&
        createPortal(
          <div ref={menuRef} className={styles.popover} style={placement}>
            <p className={styles.title}>{title}</p>
            {devices.length ? (
              <div className={styles.list} role="menu" aria-label={title}>
                {devices.map((device, index) => {
                  const label = deviceLabel(device, index)
                  const isActive = device.deviceId === selectedDeviceId

                  return (
                    <button
                      key={`${index}-${device.deviceId}`}
                      type="button"
                      role="menuitemradio"
                      aria-checked={isActive}
                      className={isActive ? `${styles.item} ${styles.itemActive}` : styles.item}
                      title={label}
                      onClick={() => handleSelect(device.deviceId)}
                    >
                      <span className={styles.label}>{label}</span>
                      <CheckIcon
                        className={
                          isActive ? styles.check : `${styles.check} ${styles.checkHidden}`
                        }
                        width={16}
                        height={16}
                        aria-hidden
                      />
                    </button>
                  )
                })}
              </div>
            ) : (
              <p className={styles.empty}>{t('chat.call.deviceMenu.empty')}</p>
            )}
          </div>,
          document.body
        )}
    </>
  )
}
